// @mui
import { styled } from "@mui/material/styles"
import { AppBar, Box, Stack, Toolbar } from "@mui/material"
// hooks
import Logo from "minimals-template/components/Logo"
import useOffSetTop from "minimals-template/components/@hooks/useOffSetTop"
import useResponsive from "minimals-template/components/@hooks/useResponsive"
// utils
import cssStyles from "minimals-template/components/utils/cssStyles"
// config
import { HEADER, NAVBAR } from "config"
// components
//
import { DashboardHeaderLeft, HeaderBarCenter, HeaderBarRight } from "slot-definitions"
import useCollapseDrawer from "minimals-template/components/@hooks/useCollapseDrawer"
import { NotInSharing } from "lib/@components/sharing/not-in-sharing"
import { isMobileApp } from "routes/@layouts/app/header/is-mobile-app"
import { resolveDashboardHeaderSlots } from "routes/@layouts/app/header/resolve-dashboard-header-slots"
import { MobileNavToggle } from "routes/@layouts/app/header/mobile-nav-toggle"

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== "isCollapse" && prop !== "isOffset" && prop !== "verticalLayout",
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
    ...cssStyles(theme).bgBlur(),
    boxShadow: "none",
    paddingTop: isMobileApp() ? 60 : 0,
    height: HEADER.MOBILE_HEIGHT,
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: theme.zIndex.appBar + 1,
    transition: theme.transitions.create(["width", "height"], {
        duration: theme.transitions.duration.shorter,
    }),
    [theme.breakpoints.up("lg")]: {
        height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
        width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
        ...(isCollapse && {
            width: `calc(100% - var(--dashboard-collapse-width, ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px))`,
        }),
        ...(isOffset && {
            height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
        }),
        ...(verticalLayout && {
            width: "100%",
            height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
            backgroundColor: theme.palette.background.default,
        }),
    },
}))

// ----------------------------------------------------------------------

export default function DashboardHeader({ onOpenSidebar, isOpenSidebar = false, verticalLayout = false, slots = {} }) {
    const { isCollapse = false } = useCollapseDrawer()
    const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout
    const isDesktop = useResponsive("up", "lg")
    const resolvedSlots = resolveDashboardHeaderSlots(
        { left: DashboardHeaderLeft, center: HeaderBarCenter, right: HeaderBarRight },
        slots
    )

    return (
        <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
            <Toolbar
                sx={{
                    minHeight: "100% !important",
                    px: { lg: 5 },
                }}
                data-cy="dashboard-header"
            >
                {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}
                {!isDesktop && (
                    <MobileNavToggle
                        onOpenSidebar={onOpenSidebar}
                        isOpenSidebar={isOpenSidebar}
                        legacyLabel="open sidebar"
                        data-cy="mobile-menu-open-button"
                    />
                )}

                <NotInSharing>
                    <resolvedSlots.left.Slot />
                </NotInSharing>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <resolvedSlots.center.Slot />
                </Box>
                <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
                    <resolvedSlots.right.Slot />
                </Stack>
            </Toolbar>
        </RootStyle>
    )
}
